import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { 
  Settings,
  User,
  Mail,
  Building2,
  Save
} from 'lucide-react';

export default function SettingsPage() {
  const { user } = useAuth();
  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    businessEmail: '',
    businessAddress: '',
    businessPhone: '',
    taxId: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Simulate save - in real app, this would save to backend
    setTimeout(() => {
      toast.success('Settings saved successfully');
      setSaving(false);
    }, 1000);
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Manage your account and business settings</p>
        </div>
      </div>

      <div className="grid gap-6 max-w-3xl">
        {/* Account Info */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-[#4F46E5]" />
              Account Information
            </CardTitle>
            <CardDescription>Your personal account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="form-grid">
              <div className="form-group">
                <Label>Full Name</Label>
                <Input
                  value={user?.name || ''}
                  disabled
                  className="bg-slate-50"
                  data-testid="user-name"
                />
              </div>
              <div className="form-group">
                <Label>Email</Label>
                <Input
                  value={user?.email || ''}
                  disabled
                  className="bg-slate-50"
                  data-testid="user-email"
                />
              </div>
            </div>
            <p className="text-sm text-slate-500">
              Account created on {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
            </p>
          </CardContent>
        </Card>

        {/* Business Info */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#4F46E5]" />
              Business Information
            </CardTitle>
            <CardDescription>This information will appear on your invoices</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="form-grid">
                <div className="form-group">
                  <Label htmlFor="businessName">Business Name</Label>
                  <Input
                    id="businessName"
                    value={businessInfo.businessName}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, businessName: e.target.value })}
                    placeholder="Your Business Name"
                    data-testid="business-name-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="businessEmail">Business Email</Label>
                  <Input
                    id="businessEmail"
                    type="email"
                    value={businessInfo.businessEmail}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, businessEmail: e.target.value })}
                    placeholder="billing@yourbusiness.com"
                    data-testid="business-email-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <Label htmlFor="businessAddress">Business Address</Label>
                <Input
                  id="businessAddress"
                  value={businessInfo.businessAddress}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, businessAddress: e.target.value })}
                  placeholder="123 Business St, City, State 12345"
                  data-testid="business-address-input"
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <Label htmlFor="businessPhone">Phone</Label>
                  <Input
                    id="businessPhone"
                    value={businessInfo.businessPhone}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, businessPhone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                    data-testid="business-phone-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="taxId">Tax ID / VAT Number</Label>
                  <Input
                    id="taxId"
                    value={businessInfo.taxId}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, taxId: e.target.value })}
                    placeholder="XX-XXXXXXX"
                    data-testid="tax-id-input"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                disabled={saving}
                data-testid="save-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Email Settings */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#4F46E5]" />
              Email Settings
            </CardTitle>
            <CardDescription>Configure email delivery for invoices</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-medium text-amber-900 mb-2">SendGrid Configuration Required</h4>
              <p className="text-sm text-amber-800 mb-3">
                To enable email delivery for invoices, you need to configure SendGrid:
              </p>
              <ol className="text-sm text-amber-800 space-y-1 list-decimal list-inside">
                <li>Create a SendGrid account at sendgrid.com</li>
                <li>Generate an API key with full access</li>
                <li>Add SENDGRID_API_KEY and SENDER_EMAIL to your backend environment</li>
                <li>Verify your sender email domain</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* App Info */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#4F46E5]" />
              About
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <p className="flex justify-between">
                <span className="text-slate-500">Application</span>
                <span className="font-medium">InvoiceGen</span>
              </p>
              <p className="flex justify-between">
                <span className="text-slate-500">Version</span>
                <span className="font-medium font-mono">1.0.0</span>
              </p>
              <p className="flex justify-between">
                <span className="text-slate-500">Features</span>
                <span className="font-medium">OpenAI Vision OCR, PDF Export, Email</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
